// Round 28-AC: post-registration landing page for end-users (global_role
// = 'none'). They can't see the admin portal — this is what they get
// instead. Shows the addin download link, install steps, and what the
// addin lets them do.

import {
  Anchor, Badge, Button, Card, Container, Group, List, Stack, Text, Title,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, clearAllAuth, getStoredUser } from '../api.js';

export function GetStartedPage(): React.JSX.Element {
  const user = getStoredUser();
  const [version, setVersion] = useState<string | null>(null);

  // Best-effort: fetch the latest addin version so the user knows what
  // they're about to install. Endpoint may not exist on older brains —
  // that's fine, just don't show a version badge.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/outlook/version.txt');
        if (r.ok) setVersion((await r.text()).trim());
      } catch { /* ignore */ }
    })();
  }, []);

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={2}>Welcome, {user?.display_name?.split(' ')[0] ?? 'there'}!</Title>
            <Text size="sm" c="dimmed">Set up the addin and you're ready to go.</Text>
          </Stack>
          <Button variant="subtle" size="sm" onClick={() => { clearAllAuth(); window.location.href = '/'; }}>
            Sign out
          </Button>
        </Group>

        <Card padding="lg" withBorder radius="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Stack gap={0}>
                <Title order={4}>1. Download the addin</Title>
                <Text size="sm" c="dimmed">Classic Outlook for Windows · ~830 KB</Text>
              </Stack>
              {version && <Badge color="gray" variant="light">v{version}</Badge>}
            </Group>
            <Button
              component="a"
              href="/outlook/ai-outlook-com-addin.zip"
              size="lg"
              leftSection={<span>⬇</span>}
              style={{ alignSelf: 'flex-start' }}
            >
              Download ai-outlook-com-addin.zip
            </Button>
            <Text size="xs" c="dimmed">
              Works with IMAP, POP, Exchange, and Microsoft 365 accounts. No admin rights required — installs per-user.
            </Text>
          </Stack>
        </Card>

        <Card padding="lg" withBorder radius="md">
          <Stack gap="md">
            <Title order={4}>2. Install</Title>
            <List spacing="sm" size="sm">
              <List.Item>
                Quit Outlook completely (system tray icon → <b>Exit</b>, not just close the window).
              </List.Item>
              <List.Item>
                Right-click the downloaded zip → <b>Properties</b> → tick <b>Unblock</b> → <b>OK</b>.
                <Text size="xs" c="dimmed">Windows blocks DLLs from the internet by default; this lets the addin register.</Text>
              </List.Item>
              <List.Item>
                Extract the zip anywhere (e.g. your Desktop).
              </List.Item>
              <List.Item>
                Double-click <b>install.bat</b>. A small window opens, runs for ~2 seconds, and closes.
              </List.Item>
              <List.Item>
                Open Outlook. Look for the <b>ai-outlook</b> tab in the ribbon — that's the addin.
              </List.Item>
            </List>
          </Stack>
        </Card>

        <Card padding="lg" withBorder radius="md">
          <Stack gap="md">
            <Title order={4}>3. Sign in</Title>
            <Text size="sm">
              Click any addin button — first time will prompt for your email + password (sent in the welcome email) and your TOTP code (Google Authenticator). After that you stay signed in.
            </Text>
          </Stack>
        </Card>

        <Card padding="lg" withBorder radius="md">
          <Stack gap="md">
            <Title order={4}>What the addin lets you do</Title>
            <List spacing="xs" size="sm">
              <List.Item><b>Action Inbox</b> — your email triaged into Act / Wait / Know / Automate / Ignore.</List.Item>
              <List.Item><b>Smart Search</b> — ask questions about your mailbox in natural language.</List.Item>
              <List.Item><b>Daily Brief</b> — every morning, a 60-second summary of what's important.</List.Item>
              <List.Item><b>Draft Reply</b> — generate context-aware replies you can edit before sending.</List.Item>
              <List.Item><b>Find Duplicates</b> — clean up duplicate copies across your mailbox.</List.Item>
              <List.Item><b>Index Folder</b> — make a folder searchable in seconds.</List.Item>
            </List>
          </Stack>
        </Card>

        <Card padding="lg" withBorder radius="md" style={{ background: 'var(--mantine-color-blue-0)' }}>
          <Stack gap="xs">
            <Title order={5}>Privacy</Title>
            <Text size="sm">
              Your mailbox stays yours. Each user only ever sees their own emails — there is no cross-user access, even for admins. All data lives on BroadNet's own server, never on third-party clouds.
            </Text>
          </Stack>
        </Card>

        <Group justify="center" gap="md">
          <Anchor component={Link} to="/" size="sm">Back to sign in</Anchor>
          <Text size="xs" c="dimmed">·</Text>
          <Anchor href="mailto:rabih@broadnet.me" size="sm">Contact admin</Anchor>
        </Group>
      </Stack>
    </Container>
  );
}
